/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { CellValue } from "@inscopix/ideas-hyperformula";
import {
  EuiFlexGroup,
  EuiHorizontalRule,
  EuiPopover,
} from "@inscopix/ideas-eui";
import { CellValueRendererBase } from "../../cell-value-renderers/CellValueRendererBase";
import { useEffect, useState } from "react";
import { FileType } from "types/FileTypes";
import { gql } from "@apollo/client";
import { CellEditorFileHeader } from "./CellEditorFileHeader";
import { CellEditorFileFooter } from "./CellEditorFileFooter";
import {
  CellEditorFileBody,
  getFilesFromCellValue,
} from "./CellEditorFileBody";

gql`
  query CellEditorFile($projectId: UUID!) {
    project: projectById(id: $projectId) {
      id
      isToolProject
      files: filesByProjectId(
        filter: {
          dateDeleted: { isNull: true }
          dateDataDeleted: { isNull: true }
          seriesParentId: { isNull: true }
        }
      ) {
        nodes {
          id
          referenceId
          name
          fileType
          tags
          source
          isSeries
          seriesFiles: filesBySeriesParentId(
            orderBy: [
              SERIES_ORDER_ASC
              # Fallback for missing order
              NAME_ASC
            ]
          ) {
            nodes {
              id
              name
            }
          }
        }
      }
    }
  }
`;

const styles = {
  popover: css`
    & {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
    }
  `,
  panel: css`
    width: 1000px;
    padding: 10px;
    max-height: max(200px, 50vh);
    overflow-y: scroll;
  `,
};

interface CellEditorFileProps {
  tableId: string;
  value: CellValue;
  fileTypes: FileType["id"][];
  fileFormats: string[];
  initialFileFilter: "all" | "unassigned";
  allowMultiSelect: boolean;
  onChangeFormula: (newFormula: string) => void;
  stopEditing: (cancel?: boolean) => void;
}

/** Cell editor for cells in file columns of analysis tables */
export const CellEditorFile = ({
  tableId,
  value,
  fileTypes,
  fileFormats,
  initialFileFilter,
  allowMultiSelect,
  onChangeFormula,
  stopEditing,
}: CellEditorFileProps) => {
  const [hideInvalidFiles, setHideInvalidFiles] = useState(true);
  const [selectedTableId, setSelectedTableId] = useState<string>();
  const [selectedRowId, setSelectedRowId] = useState<string>();
  const [selectedColumnId, setSelectedColumnId] = useState<string>();
  const [initialSelectedFiles] = useState(
    getFilesFromCellValue(value).map((file) => ({
      id: file.attrs.id,
      referenceId: file.attrs.referenceId,
    })),
  );
  const [selectedFiles, setSelectedFiles] = useState(initialSelectedFiles);

  // Calculate cell formula when file selections change
  useEffect(() => {
    // If no new files selected, do nothing
    if (selectedFiles === initialSelectedFiles) {
      return;
    } else if (selectedFiles.length === 0 && value !== null) {
      onChangeFormula("");
    } else if (selectedFiles.length === 1) {
      const refId = selectedFiles[0].referenceId;
      const fileFormula = `=FILE("${refId}")`;
      onChangeFormula(fileFormula);
    } else if (selectedFiles.length > 1) {
      const fileFormulas = selectedFiles.map(
        (file) => `FILE("${file.referenceId}")`,
      );
      const groupFormula = `=GROUP(${fileFormulas.join(",")})`;
      onChangeFormula(groupFormula);
    }
  }, [initialSelectedFiles, onChangeFormula, selectedFiles, value]);

  return (
    <EuiPopover
      anchorPosition="downCenter"
      button={<CellValueRendererBase value={value} />}
      isOpen
      panelPaddingSize="none"
      css={styles.popover}
      closePopover={() => stopEditing()}
      display="block"
      repositionOnScroll={true}
      panelProps={{ paddingSize: "none" }}
    >
      <EuiFlexGroup css={styles.panel} direction="column" gutterSize="none">
        <CellEditorFileHeader
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          onClose={() => stopEditing()}
        />

        <EuiHorizontalRule margin="s" />

        <CellEditorFileBody
          fileTypes={fileTypes}
          fileFormats={fileFormats}
          initialFileFilter={initialFileFilter}
          hideInvalidFiles={hideInvalidFiles}
          selectedTableId={selectedTableId}
          selectedRowId={selectedRowId}
          selectedColumnId={selectedColumnId}
          selectedFiles={selectedFiles}
          allowMultiSelect={allowMultiSelect}
          setSelectedTableId={setSelectedTableId}
          setSelectedRowId={setSelectedRowId}
          setSelectedColumnId={setSelectedColumnId}
          setSelectedFiles={setSelectedFiles}
        />

        <EuiHorizontalRule margin="s" />

        <CellEditorFileFooter
          setSelectedTableId={setSelectedTableId}
          setSelectedRowId={setSelectedRowId}
          setSelectedColumnId={setSelectedColumnId}
          hideInvalidFiles={hideInvalidFiles}
          setHideInvalidFiles={setHideInvalidFiles}
        />
      </EuiFlexGroup>
    </EuiPopover>
  );
};
